import { useSocket } from "../../../contexts/SocketContext";
import { useGetData } from "../../../services/api/api-tools";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader } from "../../../components/loading/Loader";
import { Select } from "../../../components/forms/Select";
import { InputNumber } from "../../../components/forms/InputNumber";
import { Button } from "../../../components/buttons/Button";
import { PlayCircleIcon } from "@heroicons/react/20/solid";
import { InputText } from "../../../components/forms/InputText";

const REQUIRED_HEADERS = ["article","operation","oven_time","oven_tolerance","transfer_time","transfer_tolerance"];

const LIVE_SETUP_STORAGE_KEY = "lisi_live_selectedWorkOrder_v1";

const readLiveSetupFromStorage = () => {
  try {
    const raw = localStorage.getItem(LIVE_SETUP_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeLiveSetupToStorage = (payload) => {
  // Store everything in a single localStorage entry.
  localStorage.setItem(LIVE_SETUP_STORAGE_KEY, JSON.stringify(payload));
};

const clearLiveSetupFromStorage = () => {
  localStorage.removeItem(LIVE_SETUP_STORAGE_KEY);
};

const readHeaderLine = async (file) => (await file.slice(0, 64 * 1024).text())
  .replace(/^\uFEFF/, "").replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n")[0] ?? "";

const parseHeaders = (line) => {
  const d = line.includes(";") && !line.includes(",") ? ";" : ",";
  return line.split(d).map(s => s.trim().replace(/^"|"$/g, "").toLowerCase());
};

const validHeaders = (hs) => hs.length === REQUIRED_HEADERS.length && REQUIRED_HEADERS.every((h, i) => hs[i] === h);

const splitCsvLine = (line, d) => { // minimal CSV splitter with quotes support
  const out = []; let cur = "", q = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') { if (q && line[i + 1] === '"') { cur += '"'; i++; } else q = !q; continue; }
    if (!q && c === d) { out.push(cur); cur = ""; continue; }
    cur += c;
  }
  out.push(cur);
  return out.map(s => s.trim());
};

const csvToPatterns = (text) => {
  const lines = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n").filter(l => l.trim() !== "");
  const headerLine = (lines.shift() || "").replace(/^\uFEFF/, "");
  const d = headerLine.includes(";") && !headerLine.includes(",") ? ";" : ",";
  const headers = headerLine.split(d).map(h => h.trim().replace(/^"|"$/g, "").toLowerCase());

  if (!(headers.length === REQUIRED_HEADERS.length && REQUIRED_HEADERS.every((h, i) => headers[i] === h))) return null;

  const idxA = headers.indexOf("article"), idxO = headers.indexOf("operation");

  return lines.map((line) => {
    const cols = splitCsvLine(line, d).map(v => v.replace(/^"|"$/g, "").trim());
    const article = cols[idxA] ?? "", operation = cols[idxO] ?? "";

    const pattern_data = headers
      .map((h, i) => {
        if (h === "article" || h === "operation") return null;
        let v = (cols[i] ?? "").trim();
        if (h === "oven_time" || h === "oven_tolerance") v = String((parseFloat(v || "0") || 0) * 60);
        return { name: h, value: v };
      })
      .filter(Boolean);

    return { name: `${article} [${operation}]`, workstation_id: 1, pattern_data };
  }).filter(p => p.name !== " []");
};

const CsvDropzone = () => {

  const inputRef = useRef(null);
  const [isHover, setIsHover] = useState(false);
  const [isDraggingCsv, setIsDraggingCsv] = useState(false);

  const openFilePicker = () => inputRef.current?.click();

  const isCsvFileLike = (file) => {
    if (!file) return false;
    const nameOk = (file.name || "").toLowerCase().endsWith(".csv");
    const typeOk =
      (file.type || "") === "text/csv" ||
      (file.type || "") === "application/vnd.ms-excel" || // some browsers
      (file.type || "") === "";
    return nameOk || typeOk;
  };

  const hasCsvInDataTransfer = (dt) => {
    if (!dt) return false;

    // Best signal when items are available
    const items = Array.from(dt.items || []);
    if (items.length) {
      return items.some((it) => {
        if (it.kind !== "file") return false;
        const entry = it.webkitGetAsEntry?.();
        if (entry?.isDirectory) return false;

        const f = it.getAsFile?.();
        if (f) return isCsvFileLike(f);

        // During dragenter some browsers don't expose the file yet; we'll validate on drop.
        return true;
      });
    }

    // Fallback: just detect "Files"
    const types = Array.from(dt.types || []);
    return types.includes("Files");
  };

  const onDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingCsv(hasCsvInDataTransfer(e.dataTransfer));
  }, []);

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    const ok = hasCsvInDataTransfer(e.dataTransfer);
    setIsDraggingCsv(ok);
    e.dataTransfer.dropEffect = ok ? "copy" : "none";
  }, []);

  const onDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingCsv(false);
  }, []);

  const handleFile = useCallback(async (file) => {
    if (!file || !isCsvFileLike(file)) return;

    const hs = parseHeaders(await readHeaderLine(file));
    if (!validHeaders(hs)){
      alert('Le format du fichier importé est incorrect.');
      return;
    }

    const patterns = csvToPatterns(await file.text());
    if (!patterns) return;

    await fetch(`${process.env.REACT_APP_API_ADDRESS}patterns/list`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify(patterns),
    });

    // location.reload();
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault(); e.stopPropagation(); setIsDraggingCsv(false);
    handleFile(e.dataTransfer?.files?.[0] ?? null);
  }, [handleFile]);

  const onInputChange = useCallback((e) => {
    handleFile(e.target.files?.[0] ?? null);
    e.target.value = "";
  }, [handleFile]);

  const bgColor = isDraggingCsv || isHover ? 'bg-white' : 'bg-gray-100';
  const borderColor = isDraggingCsv ? 'border-perception-blue' : 'border-gray-200';
  const textColor = isDraggingCsv ? 'text-perception-blue' : isHover ? 'text-perception-gray-700' : '';
  const transform = isDraggingCsv || isHover ? 'scale-105' : '';

  return (
    <div>
      <h1 className="mb-8 font-semibold text-perception-blue">Importer de nouvelles gammes</h1>

      <input ref={inputRef} type="file" accept=".csv,text/csv" onChange={onInputChange} style={{ display: "none" }}/>

      <div
        role="button"
        tabIndex={0}
        onClick={openFilePicker}
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        aria-label="Importer un fichier CSV"
        className={`h-36 flex items-center px-16 ${borderColor} border-dashed border-2 ${bgColor} ${textColor} rounded-md transition-all duration-300 ${transform}`}
      >
        <div>
          <div>
            Glisser & déposer un fichier <span style={{ fontFamily: "monospace" }}>.csv</span>
          </div>
          <div>ou cliquer pour sélectionner</div>
        </div>
      </div>

      <div className="text-xs mt-2">
        Seuls les fichiers CSV sont acceptés.
      </div>
    </div>
  );
}


export const LISILiveSetupPage = () => {

  const { sendEvent } = useSocket();

  const [selectedArticle, setSelectedArticle] = useState(null);
  const [selectedOperation, setSelectedOperation] = useState(null);
  const [selectedAMode, setSelectedAMode] = useState(null);
  const [ofValue, setOfValue] = useState('');
  const [quantity, setQuantity] = useState(0);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const startDate = useRef(null);

  const { data: patterns, isLoading: patternsAreLoading } = useGetData('patterns', `patterns`);

  const _patternToRow = (pattern) => {
    const name = String(pattern?.name ?? "");
    const m = name.match(/^(.*?)\s*\[(.*?)\]\s*$/);
    const row = { id: pattern.id, article: (m?.[1] ?? name).trim(), operation: (m?.[2] ?? '').trim() };
    (pattern?.pattern_data ?? []).forEach(({ name, value }) => { if (name) row[name] = parseInt(value); });
    return row;
  };
  const patternsWithData = patternsAreLoading ? [] : patterns.map(_patternToRow);

  const articles = useMemo(() => (
    Object.values(
      patternsWithData.reduce((acc, { id, article, operation, ...rest }) => {
        (acc[article] ||= { id: article, name: article, operations: [] }).operations.push({
          id: operation,
          pattern_id: id,
          name: operation,
          ...rest
        });
        return acc;
      }, {})
    )
  ), [patternsWithData]);

  useEffect(() => {
    if (patternsAreLoading) return;
    if (selectedWorkOrder) return;

    const saved = readLiveSetupFromStorage();
    if (!saved?.selectedWorkOrder) return;

    const articleName = saved?.selectedArticle?.name ?? "";
    const operationPatternId = saved?.selectedOperation?.pattern_id ?? null;
    const operationName = saved?.selectedOperation?.name ?? "";

    const restoredArticle = articles.find((a) => a?.name === articleName) ?? null;
    const restoredOperation = restoredArticle
      ? (restoredArticle.operations.find((op) => op?.pattern_id === operationPatternId)
        ?? restoredArticle.operations.find((op) => op?.name === operationName)
        ?? null)
      : null;

    setSelectedArticle(restoredArticle);
    setSelectedOperation(restoredOperation);
    setSelectedAMode(saved?.selectedAMode ? { id: saved.selectedAMode.id } : null);
    setOfValue(saved?.ofValue ?? "");
    setQuantity(saved?.quantity ?? 0);
    setSelectedWorkOrder(saved.selectedWorkOrder);
    startDate.current = saved?.startDate ?? null;

    sendEvent('current_data', { 'start_production': saved.selectedWorkOrder });
  }, [patternsAreLoading, selectedWorkOrder, articles, sendEvent]);

  const handleStart = () => {

    /* Send work order data to the engine */
    const selectedOperationSeconds = { ...selectedOperation, oven_time: selectedOperation.oven_time, oven_tolerance: selectedOperation.oven_tolerance };
    const selectedWorkOrder = {
      ...selectedOperationSeconds,
      pattern_id: selectedOperation.pattern_id,
      name: '#' + ofValue + ' - ' + selectedArticle.name + ' (' + selectedOperation.name + ') (' + selectedAMode.id + ')',
      configuration: selectedAMode.id,
      quantity
    };

    const startTs = Math.floor(Date.now() / 1000);

    writeLiveSetupToStorage({
      startDate: startTs,
      ofValue,
      quantity,
      selectedAMode: selectedAMode ? { id: selectedAMode.id } : null,
      selectedArticle: selectedArticle ? { id: selectedArticle.id, name: selectedArticle.name } : null,
      selectedOperation: selectedOperation ? { id: selectedOperation.id, name: selectedOperation.name, pattern_id: selectedOperation.pattern_id } : null,
      selectedWorkOrder,
    });

    sendEvent('current_data', { 'start_production': selectedWorkOrder});
    setSelectedWorkOrder(selectedWorkOrder);
    startDate.current = startTs;
  };

  const handleStop = async () => {
    await fetch(`${process.env.REACT_APP_API_ADDRESS}pattern_executions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify({
        pattern: `/api/patterns/${selectedWorkOrder.pattern_id}`,
        start_date: startDate.current,
        stop_date: Math.floor(Date.now() / 1000),
        data: {quantity: quantity, a_mode: selectedAMode.id, of: ofValue}
      }),
    });

    clearLiveSetupFromStorage();

    sendEvent('current_data', { 'stop_production': null });
    setSelectedArticle(null);
    setSelectedOperation(null);
    setOfValue('');
    setQuantity(0);
    setSelectedWorkOrder(null);
    setSelectedAMode(null);
    startDate.current = null;
  };

  if (patternsWithData.length === 0) return <Loader/>

  return (
    <div className="h-full bg-gray-100 flex flex-col gap-16 justify-center items-center text-center text-xl text-perception-gray-700">
      {
        !selectedWorkOrder ?
          <div className="flex flex-col justify-center gap-16 h-full">
            <div className="flex flex-col items-center gap-8 p-8 rounded-md bg-white">
              <h1 className="font-semibold text-perception-blue">Démarrer une production</h1>
              <div className="flex items-center gap-4">
                <Select
                  items={articles}
                  value={selectedArticle}
                  setValue={setSelectedArticle}
                  itemToString={(item) => item?.name}
                  placeholder="Selectionner un article"
                  size="lg"
                />
                <Select
                  items={selectedArticle?.operations}
                  value={selectedOperation}
                  setValue={setSelectedOperation}
                  itemToString={(item) => item?.name}
                  placeholder="Selectionner une opération"
                  disabled={selectedArticle === null}
                  size="lg"
                />
                <Select
                  items={[{ id: 'A1' }, { id: 'A2' }]}
                  value={selectedAMode}
                  setValue={setSelectedAMode}
                  itemToString={(item) => item.id}
                  placeholder="A1/A2"
                  disabled={selectedOperation === null}
                  size="lg"
                />
              </div>

              <div className="flex items-center gap-4">
                OF : <InputText disabled={selectedOperation === null}
                                value={ofValue} setValue={setOfValue}
                                size="lg" placeholder="#12345ABCDEF"/>

                Quantité : <InputNumber disabled={selectedOperation === null}
                                        value={quantity} setValue={setQuantity}
                                        size="lg"/>
              </div>

              <div>
                <Button type='submit' category='primary-btn' size='small' disabled={ofValue === '' || !(quantity > 0)} onClick={handleStart}>
                  Démarrer la production
                </Button>
              </div>
            </div>

            <div className="flex flex-col items-center gap-8 p-8 bg-white rounded-md">
              <CsvDropzone/>
            </div>
          </div>
          :
          <div className="flex flex-col items-center gap-16">
            <PlayCircleIcon className="size-64 text-perception-gray-500"/>
            <div>
              <p>Une production est en cours :</p>
              <p className="text-xl text-perception-blue">{selectedWorkOrder.name} (Quantité : {selectedWorkOrder.quantity})</p>
            </div>
            <Button type='submit' category='primary-btn' size='small' onClick={handleStop} className="w-64">
              Arrêter la production
            </Button>
          </div>
      }
    </div>
  );
};